import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import type { AuthChangeEvent, User } from "@supabase/supabase-js";
import { supabase, supabaseConfigured } from "../lib/supabase";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  configured: boolean;
  passwordRecovery: boolean;
  clearPasswordRecovery: () => void;
  signIn: (email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const RECOVERY_SESSION_KEY = "core-saas-password-recovery";

function urlSignalsPasswordRecovery() {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(
    window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash,
  );

  return (
    search.get("type") === "recovery" ||
    hash.get("type") === "recovery"
  );
}

function readStoredRecoveryState() {
  return sessionStorage.getItem(RECOVERY_SESSION_KEY) === "1";
}

function storeRecoveryState(active: boolean) {
  if (active) {
    sessionStorage.setItem(RECOVERY_SESSION_KEY, "1");
  } else {
    sessionStorage.removeItem(RECOVERY_SESSION_KEY);
  }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(supabaseConfigured);
  const [passwordRecovery, setPasswordRecovery] = useState(
    () => urlSignalsPasswordRecovery() || readStoredRecoveryState(),
  );

  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return;
    }

    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session) => {
        if (!active) return;

        setUser(session?.user ?? null);
        setLoading(false);

        if (event === "PASSWORD_RECOVERY") {
          storeRecoveryState(true);
          setPasswordRecovery(true);
        }

        if (event === "SIGNED_OUT") {
          storeRecoveryState(false);
          setPasswordRecovery(false);
        }
      },
    );

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: supabaseConfigured,
      passwordRecovery,
      clearPasswordRecovery() {
        storeRecoveryState(false);
        setPasswordRecovery(false);
      },
      async signIn(email, password) {
        storeRecoveryState(false);
        setPasswordRecovery(false);

        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });

        return error?.message ?? null;
      },
      async signOut() {
        storeRecoveryState(false);
        setPasswordRecovery(false);
        await supabase.auth.signOut();
      },
    }),
    [user, loading, passwordRecovery],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
